/*
 * ReferralLinks.jsx — admin management for referral intake links.
 *
 * Iter 163. Route: /referral-links (inside Layout). Every non-pending, non-
 * read_only role can open this. OD sees only their clinic's links.
 */
import { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { toast } from 'sonner';
import { Copy, Plus, Power, Trash2, Link2, ExternalLink, QrCode, Baby } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, Tooltip } from 'recharts';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../components/ui/dropdown-menu';

import { getOrg } from '../lib/org';

const THRIVE_CATEGORIES = ['Pediatrician', 'ABA Clinic', 'Daycare', 'School', 'Physician / Other'];
const THRIVE_CLINICS = ['Katy', 'Missouri City', 'Townelake', 'Fry Rd', 'Memorial', 'Brookshire', 'Tomball', 'Magnolia', 'Atascocita'];
const IS_LUDO = () => getOrg() === 'ludo';

function publicUrl(token) {
  const origin = window.location.origin;
  return `${origin}/refer/${token}${IS_LUDO() ? '?org=ludo' : ''}`;
}

const inputCls = 'h-10 rounded-lg border border-[#E4DBE8] bg-white px-3 text-sm text-[#2D2833] outline-none focus:border-[#685B72] focus:ring-2 focus:ring-[#BAA9CA]/30 w-full';

export default function ReferralLinks() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ partner_name: '', category: 'Pediatrician', clinic: '' });
  const [opts, setOpts] = useState({ categories: THRIVE_CATEGORIES, clinics: THRIVE_CLINICS });
  useEffect(() => { api.get('/referral-links/options').then(r => setOpts(r.data)).catch(() => {}); }, []);
  const CATEGORIES = opts.categories;

  const clinicOptions = useMemo(() => {
    const all = opts.clinics;
    if (!user || IS_LUDO()) return all;
    if (user.role === 'office_director') {
      const list = user.clinics?.length ? user.clinics : (user.clinic ? [user.clinic] : []);
      return all.filter(c => list.includes(c));
    }
    return all;
  }, [user, opts]);

  useEffect(() => {
    if (clinicOptions.length && !clinicOptions.includes(draft.clinic)) setDraft(d => ({ ...d, clinic: clinicOptions[0] }));
  }, [clinicOptions, draft.clinic]);
  useEffect(() => {
    if (CATEGORIES.length && !CATEGORIES.includes(draft.category)) setDraft(d => ({ ...d, category: CATEGORIES[0] }));
  }, [CATEGORIES, draft.category]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/referral-links');
      setRows(r.data?.links || []);
    } catch (e) {
      toast.error('Could not load referral links.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.clinic || !draft.category) return;
    setCreating(true);
    try {
      const r = await api.post('/referral-links', {
        partner_name: draft.partner_name.trim(),
        category: draft.category,
        clinic: draft.clinic,
      });
      setRows(prev => [r.data, ...prev]);
      setDraft(d => ({ ...d, partner_name: '' }));
      toast.success('Referral link created');
      // Immediately copy the new link so user can share it.
      const url = publicUrl(r.data.token);
      navigator.clipboard?.writeText(url).catch(() => {});
      toast.message('Link copied to clipboard', { description: url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create link.');
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (row) => {
    try {
      const r = await api.patch(`/referral-links/${row.link_id}`, { active: !row.active });
      setRows(prev => prev.map(x => x.link_id === row.link_id ? { ...x, ...r.data } : x));
      toast.success(row.active ? 'Link deactivated' : 'Link reactivated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update link.');
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete referral link for ${row.partner_name || row.category}? This cannot be undone.`)) return;
    try {
      await api.delete(`/referral-links/${row.link_id}`);
      setRows(prev => prev.filter(x => x.link_id !== row.link_id));
      toast.success('Link deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete link.');
    }
  };

  const copyUrl = (row) => {
    const url = publicUrl(row.token);
    navigator.clipboard?.writeText(url).then(
      () => toast.success('Link copied', { description: url }),
      () => toast.error('Could not copy — please copy manually'),
    );
  };

  // Trigger the browser download for the branded QR poster PNG. Uses the
  // authenticated axios client so we go through the same auth cookie/
  // header path the rest of the app uses, then convert to a Blob URL.
  // `variant` is 'provider' or 'family'; `lang` applies only to family.
  const downloadPoster = async (row, variant = 'provider', lang = 'en') => {
    const base = variant === 'family'
      ? `/referral-links/${row.link_id}/family-poster.png`
      : `/referral-links/${row.link_id}/poster.png`;
    const endpoint = variant === 'family' && lang === 'es' ? `${base}?lang=es` : base;
    try {
      const r = await api.get(endpoint, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      const safe = (row.partner_name || row.category || 'referral').replace(/\s+/g, '_').replace(/\//g, '-');
      const brand = IS_LUDO() ? 'ludo' : 'thrive';
      const prefix = variant === 'family'
        ? (lang === 'es' ? `${brand}_family_qr_es` : `${brand}_family_qr`)
        : `${brand}_referral`;
      a.download = `${prefix}_${safe}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      const toastMsg = variant === 'family'
        ? (lang === 'es' ? 'Póster familiar (Español) descargado' : 'Family QR poster (English) downloaded')
        : 'Referral poster downloaded';
      toast.success(toastMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not download poster.');
    }
  };

  return (
    <div className="space-y-8 max-w-6xl mx-auto" data-testid="referral-links-page">
      <div>
        <h1 className="font-serif text-3xl text-[#2D2833] flex items-center gap-2">
          <Link2 size={22} className="text-[#685B72]" /> {IS_LUDO() ? 'Ludo ABA referral links' : 'Referral intake links'}
        </h1>
        <p className="text-sm text-[#685B72] mt-2 max-w-2xl leading-relaxed">
          Generate a unique shareable link for any referring partner. When they fill out the form, the submission
          drops directly into the Inquiries queue tagged with your partner and clinic — no manual entry needed.
        </p>
      </div>

      {/* ── Create form ─────────────────────────────────────────────── */}
      <section className="bg-white/80 rounded-2xl border border-white/60 shadow-sm p-5 sm:p-6" data-testid="create-link-section">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-7 w-7 rounded-lg bg-[#BAA9CA]/30 text-[#52475A] flex items-center justify-center">
            <Plus size={16} />
          </div>
          <h2 className="font-serif text-lg text-[#2D2833]">Create a new link</h2>
        </div>
        <div className="grid sm:grid-cols-4 gap-3">
          <div className="sm:col-span-2">
            <label className="block text-xs uppercase tracking-wider text-[#685B72] mb-1.5 font-semibold">Partner name (optional)</label>
            <input
              className={inputCls}
              placeholder="e.g. Townelake Pediatrics"
              value={draft.partner_name}
              onChange={e => setDraft(d => ({ ...d, partner_name: e.target.value }))}
              data-testid="new-link-partner-input"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#685B72] mb-1.5 font-semibold">Category</label>
            <select
              className={inputCls}
              value={draft.category}
              onChange={e => setDraft(d => ({ ...d, category: e.target.value }))}
              data-testid="new-link-category-select"
            >
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#685B72] mb-1.5 font-semibold">Destination clinic</label>
            <select
              className={inputCls}
              value={draft.clinic}
              onChange={e => setDraft(d => ({ ...d, clinic: e.target.value }))}
              data-testid="new-link-clinic-select"
            >
              {clinicOptions.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-end gap-3">
          <span className="text-xs text-[#685B72]">The link is copied to your clipboard when created.</span>
          <button
            onClick={create}
            disabled={creating || !draft.clinic}
            className="h-10 px-5 rounded-lg bg-[#2D2833] text-white text-sm font-semibold hover:bg-[#52475A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="create-link-btn"
          >
            {creating ? 'Creating…' : 'Create link'}
          </button>
        </div>
      </section>

      {/* ── List ─────────────────────────────────────────────────────── */}
      <section className="bg-white/80 rounded-2xl border border-white/60 shadow-sm overflow-hidden" data-testid="links-list-section">
        <div className="p-5 sm:p-6 border-b border-white/60 flex items-center justify-between">
          <h2 className="font-serif text-lg text-[#2D2833]">Your links</h2>
          <span className="text-xs text-[#685B72]">{rows.length} link{rows.length === 1 ? '' : 's'}</span>
        </div>
        {loading ? (
          <div className="p-8 text-sm text-[#685B72]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-sm text-[#685B72]" data-testid="links-empty">No referral links yet — create your first one above.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="links-table">
              <thead className="bg-[#F6F1EE]/70">
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#685B72]">
                  <th className="px-5 py-3 font-semibold">Partner</th>
                  <th className="px-5 py-3 font-semibold">Category</th>
                  <th className="px-5 py-3 font-semibold">Clinic</th>
                  <th className="px-5 py-3 font-semibold text-right">Submissions</th>
                  <th className="px-5 py-3 font-semibold">Last 30 days</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.link_id} className="border-t border-white/60 hover:bg-white/60" data-testid={`link-row-${row.link_id}`}>
                    <td className="px-5 py-3 text-[#2D2833]">
                      <div className="font-semibold">{row.partner_name || <span className="text-[#685B72] italic">Unnamed partner</span>}</div>
                      <a
                        href={publicUrl(row.token)}
                        target="_blank" rel="noreferrer"
                        className="text-[11px] text-[#685B72] hover:text-[#52475A] inline-flex items-center gap-1 mt-0.5"
                        data-testid={`link-open-${row.link_id}`}
                      >
                        /refer/{row.token} <ExternalLink size={11} />
                      </a>
                    </td>
                    <td className="px-5 py-3 text-[#2D2833]">{row.category}</td>
                    <td className="px-5 py-3 text-[#2D2833]">{row.clinic}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-[#2D2833] font-semibold">{row.submission_count}</td>
                    <td className="px-5 py-3">
                      <Sparkline linkId={row.link_id} />
                    </td>
                    <td className="px-5 py-3">
                      {row.active
                        ? <span className="inline-block rounded-full bg-[#B7D6C7]/50 text-[#3C6152] px-2.5 py-0.5 text-[11px] font-semibold">Active</span>
                        : <span className="inline-block rounded-full bg-[#E4DBE8] text-[#685B72] px-2.5 py-0.5 text-[11px] font-semibold">Inactive</span>}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => copyUrl(row)}
                          className="h-8 w-8 rounded-md text-[#685B72] hover:bg-[#BAA9CA]/25 hover:text-[#52475A] flex items-center justify-center transition-colors"
                          title="Copy link"
                          data-testid={`link-copy-${row.link_id}`}
                        >
                          <Copy size={15} />
                        </button>
                        <button
                          onClick={() => downloadPoster(row, 'provider')}
                          className="h-8 w-8 rounded-md text-[#685B72] hover:bg-[#BAA9CA]/25 hover:text-[#52475A] flex items-center justify-center transition-colors"
                          title="Download QR poster for the referring provider (form for THEIR intake)"
                          data-testid={`link-poster-${row.link_id}`}
                        >
                          <QrCode size={15} />
                        </button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className="h-8 w-8 rounded-md text-[#685B72] hover:bg-[#BAA9CA]/25 hover:text-[#52475A] flex items-center justify-center transition-colors"
                              title="Download family-facing QR poster (English or Español)"
                              data-testid={`link-family-poster-${row.link_id}`}
                            >
                              <Baby size={15} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="min-w-[220px]">
                            <DropdownMenuItem
                              onClick={() => downloadPoster(row, 'family', 'en')}
                              data-testid={`link-family-poster-en-${row.link_id}`}
                            >
                              <Baby size={14} className="mr-2 opacity-70" />
                              Family poster · English
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => downloadPoster(row, 'family', 'es')}
                              data-testid={`link-family-poster-es-${row.link_id}`}
                            >
                              <Baby size={14} className="mr-2 opacity-70" />
                              Póster familiar · Español
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <button
                          onClick={() => toggle(row)}
                          className="h-8 w-8 rounded-md text-[#685B72] hover:bg-[#BAA9CA]/25 hover:text-[#52475A] flex items-center justify-center transition-colors"
                          title={row.active ? 'Deactivate' : 'Reactivate'}
                          data-testid={`link-toggle-${row.link_id}`}
                        >
                          <Power size={15} />
                        </button>
                        <button
                          onClick={() => remove(row)}
                          disabled={row.submission_count > 0}
                          className="h-8 w-8 rounded-md text-[#B54B4B] hover:bg-[#B54B4B]/10 flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title={row.submission_count > 0 ? 'Cannot delete — deactivate instead' : 'Delete'}
                          data-testid={`link-delete-${row.link_id}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// ─── Sparkline (30-day submissions per link) ──────────────────────────
// Loads its own data on mount so the parent doesn't need to fan out N
// requests before the first paint. Renders a tiny inline chart plus the
// running 30-day total so admin can see traction at a glance.
function Sparkline({ linkId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/referral-links/${linkId}/history?days=30`)
      .then(r => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ series: [], total: 0 }); });
    return () => { alive = false; };
  }, [linkId]);

  if (!data) {
    return <div className="h-8 w-32 rounded bg-[#F6F1EE] animate-pulse" data-testid={`spark-loading-${linkId}`} />;
  }
  if (!data.total) {
    return <span className="text-[11px] text-[#685B72] italic" data-testid={`spark-empty-${linkId}`}>No submissions yet</span>;
  }
  return (
    <div className="flex items-center gap-3" data-testid={`spark-${linkId}`}>
      <div className="w-32 h-9">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data.series} margin={{ top: 4, right: 2, bottom: 4, left: 2 }}>
            <Tooltip
              contentStyle={{ background: '#FFF', border: '1px solid #E4DBE8', borderRadius: 8, fontSize: 11, padding: '4px 8px' }}
              labelFormatter={v => v}
              formatter={v => [v, 'submissions']}
              cursor={{ stroke: '#BAA9CA', strokeWidth: 1 }}
            />
            <Line
              type="monotone"
              dataKey="count"
              stroke="#685B72"
              strokeWidth={1.75}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <span className="text-[11px] text-[#685B72] tabular-nums" title="Last 30 days">
        <span className="font-semibold text-[#2D2833]">{data.total}</span>
        <span className="ml-0.5">/ 30d</span>
      </span>
    </div>
  );
}
